import { supabase } from '@/lib/supabase/client'

const BUCKET = 'job-images'
const MAX_EDGE_PX = 2000
const JPEG_QUALITY = 0.85
const SIGNED_URL_TTL_S = 60 * 60

export const MAX_JOB_IMAGES = 6

const loadImage = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Kunne ikke lese bildet'))
    }
    img.src = url
  })

/** Downscales phone photos so storage and the control card PDF stay small. */
async function toJpeg(file: File): Promise<Blob> {
  const img = await loadImage(file)
  const scale = Math.min(1, MAX_EDGE_PX / Math.max(img.naturalWidth, img.naturalHeight))
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(img.naturalWidth * scale)
  canvas.height = Math.round(img.naturalHeight * scale)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Kunne ikke behandle bildet')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Kunne ikke behandle bildet'))), 'image/jpeg', JPEG_QUALITY),
  )
}

/** Uploads a photo for the assignment and returns its storage path. */
export async function uploadJobImage(assignmentId: string, file: File): Promise<string> {
  if (!file.type.startsWith('image/')) throw new Error('Velg en bildefil (JPG, PNG, osv.)')
  const blob = await toJpeg(file)
  const path = `${assignmentId}/${crypto.randomUUID()}.jpg`
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, { contentType: 'image/jpeg' })
  if (error) throw error
  return path
}

/** Best effort: a leftover file is harmless, so failures are only logged. */
export async function removeJobImages(paths: string[]) {
  if (paths.length === 0) return
  const { error } = await supabase.storage.from(BUCKET).remove(paths)
  if (error) console.error('Removing job images failed:', error)
}

export async function getJobImageUrls(paths: string[]): Promise<Record<string, string>> {
  if (paths.length === 0) return {}
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrls(paths, SIGNED_URL_TTL_S)
  if (error) throw error
  return Object.fromEntries(
    (data ?? []).flatMap((item) => (item.path && item.signedUrl ? [[item.path, item.signedUrl]] : [])),
  )
}

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Kunne ikke lese bildet'))
    reader.readAsDataURL(blob)
  })

/** Data URLs in the given order, for embedding in the control card. */
export async function downloadJobImages(paths: string[]): Promise<string[]> {
  return Promise.all(
    paths.map(async (path) => {
      const { data, error } = await supabase.storage.from(BUCKET).download(path)
      if (error || !data) throw new Error(`Kunne ikke hente bilde: ${error?.message ?? path}`)
      return blobToDataUrl(data)
    }),
  )
}
